/**
 * Centralized z-index management for Neoni frontend.
 *
 * All z-index values must be defined here to ensure a single source of truth
 * across the entire application. This prevents conflicts where sticky headers,
 * modals, and dropdowns render in unexpected stacking order.
 *
 * STACKING HIERARCHY (low to high):
 * - 0 (NORMAL_CONTENT): Default flow, standard page elements
 * - 5 (TABLE_STICKY_COLUMN): Sticky first column of a scrollable table (SimpleSortTable)
 * - 10 (STICKY_HEADER): Page sticky headers (e.g., UniversalPageLayout Zone 1)
 * - 50 (MODAL_OVERLAY): Modal overlays and modal content containers
 * - 60 (DROPDOWN_MENU): Dropdowns and combobox menus (SearchableSelect, ISICSelector)
 * - 1 (MODAL_CLOSE_BUTTON): local value scoped inside a single modal, not part of the page-level scale above
 *
 * USAGE:
 * - Import as `import { zIndex } from '@/lib/zIndex'` or const/import patterns
 * - Use with Tailwind classes: `z-50` (from tailwind.config.js extension)
 * - Use in styled-components/inline styles: `zIndex: zIndex.MODAL_OVERLAY`
 * - Never hardcode z-index values directly in components or CSS
 *
 * WHY THIS MATTERS:
 * When a modal opens on a page with a sticky header, the overlay (background
 * darkening) must render BEHIND the modal content but ABOVE all page elements
 * (including the sticky header). Without a centralized z-index hierarchy:
 * - Sticky headers (z-10) would appear above modal overlays (no z-index = auto/0)
 * - Dropdowns would sometimes appear above modals or below sticky headers
 * - Each new component adds uncertainty about what value to pick
 *
 * DROPDOWN_MENU > MODAL_OVERLAY (not the reverse) because SearchableSelect always portals its
 * open menu to `document.body` (`menuPortalTarget`) regardless of where the control itself is
 * rendered — so a dropdown used as a form field inside a modal is NOT a DOM descendant of that
 * modal's stacking context, it's a body-level sibling of the overlay. If DROPDOWN_MENU were lower
 * than MODAL_OVERLAY, every dropdown used inside a modal would render visually behind the modal's
 * own overlay: greyed out and unclickable (confirmed bug - see training session modals). An open
 * menu is always the element the user is actively interacting with, so it must win against
 * whatever currently-open modal happens to contain it.
 *
 * This system ensures modals always darken the entire page correctly and
 * all overlapping elements render in the intended stacking order.
 */

export const zIndex = {
	/** Default/normal content flow. Use sparingly (prefer no z-index unless competing with another layer). */
	NORMAL_CONTENT: 0,

	/**
	 * Sticky first column of a horizontally-scrollable table (so it occludes other columns'
	 * content sliding underneath it). Must stay BELOW STICKY_HEADER - a table that scrolls
	 * vertically with the page can have its sticky column visually overlap a page-level sticky
	 * header (e.g. UniversalPageLayout Zone 1), and the sticky column must slide underneath that
	 * header rather than rendering on top of it.
	 * Used by: SimpleSortTable's sticky first column/header cell.
	 */
	TABLE_STICKY_COLUMN: 5,

	/**
	 * Sticky page headers that stay visible while scrolling.
	 * Must be above normal content but below overlays so modals can darken content behind them.
	 * Used by: UniversalPageLayout Zone 1 (sticky heading), page headers with sticky positioning.
	 */
	STICKY_HEADER: 10,

	/**
	 * Modal overlays (the darkened background) and modal content containers.
	 * Must be above sticky headers/normal content so modals darken the whole page.
	 * Used by: Modal.tsx (overlay), CanonicalModal, ConfirmationModal.
	 */
	MODAL_OVERLAY: 50,

	/**
	 * Dropdown menus, comboboxes, and select options. Always portaled to `document.body` (see
	 * SearchableSelect's `menuPortalTarget`), so must be the single highest layer - higher even
	 * than MODAL_OVERLAY - or a dropdown used inside a modal renders behind that modal's own
	 * overlay (greyed out, unclickable) since the portal makes it a body-level sibling, not a
	 * descendant, of the modal's stacking context.
	 * Used by: SearchableSelect, ISICSelector, react-select dropdowns.
	 */
	DROPDOWN_MENU: 60,

	/**
	 * Local value scoped to a single modal's own content box, not part of the page-level
	 * hierarchy above. Ensures the close button always renders above its sibling modal
	 * content (which is itself position:relative with z-index:auto for other reasons,
	 * e.g. CanonicalModal's drag-overlay wrapper) regardless of DOM order.
	 * Used by: Modal.tsx (.isumio-closebutton in Modal.css).
	 */
	MODAL_CLOSE_BUTTON: 1,
} as const

/**
 * Type-safe z-index value from the hierarchy (useful for TypeScript components).
 */
export type ZIndexValue = (typeof zIndex)[keyof typeof zIndex]

export default zIndex
